import axios from "axios"
import moment from "moment"
import type React from "react"
import { Component } from "react"
import {
	Badge,
	Button,
	FormControl,
	Modal,
	OverlayTrigger,
	Table,
	Tooltip,
} from "react-bootstrap"
import { shouldAllowAccess } from "src/utilities/helper"
import AxiosConfig from "../../../../utilities/AxiosConfig"
import "./VehicleLeaseInformation.css"
import type { I_Attachment } from "src/common_interfaces/I_Attachment"
import type {
	I_VehicleLease,
	I_VehicleLeaseCalculationResponse,
} from "src/common_interfaces/I_VehicleLease"
import Loader from "src/components/common/Loader"
import IconBuilder from "src/utilities/iconBuilder/IconBuilder"
import reggie from "src/utilities/Reggie"
import Dialog from "src/utilities/Dialog"
import DocUploader from "../../FleetUtilities/DocUploader/DocUploader"
import LeaseInformationView from "../../LeaseTracking/LeaseInformationView/LeaseInformationView"
import RenameUnitNumberButton from "./RenameUnitNumberButton"
import openLink from "src/utilities/openLink"

interface VehicleLeaseInformationProps {
	leases: Array<any>
	leasesLoading: boolean
	info: any
	userId: any
	refreshAll: Function
}

type VehicleLeaseInformationTypes = {
	selectedLease: I_VehicleLease | null
	calculations: I_VehicleLeaseCalculationResponse | null
	calculationsLoading: boolean
	proposedLease: I_VehicleLease | null
	proposedCalculations: I_VehicleLeaseCalculationResponse | null
	showForm: boolean
	editing: boolean
	submitting: boolean
	confirmTerminate: boolean
	UnitNumber: string
	DateReceived: string
	LeaseLength: string
	MonthOfFirstPayment: string
	CapitalizedAmount: string
	LeaseDepreciationRate: string
	InterestRate: string
	MgmtFeePercent: string
	MaintenanceProgramMonthly: string
	SalesTaxPerMonth: string
	SalesTaxOnIncentive: string
	PeriodZeroLeaseExpense: string
	PeriodZeroDepreciation: string
	EndOfLeaseServiceCharge: string
	EFMNegotiatedRate: string
	DownPayment: string
	GainOnPriorVehicle: string
	leaseRetirementDate: string
	overrideStep: number
	calculatedPayment: number | null
	overrideValue: string
	savedLeaseId: number | null
	errorMessage: string
	leaseTerminationRecordTypeId: number | null
	showPreview: boolean
	attachments: boolean
	leaseReceipts: I_Attachment[]
	filePath: string | null
	fileName: string | null
}

class VehicleLeaseInformation extends Component<
	VehicleLeaseInformationProps,
	VehicleLeaseInformationTypes
> {
	storageLocation = process.env.REACT_APP_LEASE_FILES_STORAGE_LOCATION
	containerName = process.env.REACT_APP_LEASE_FILES_CONTAINER_NAME
	fileStoreLocationId = process.env.REACT_APP_LEASE_FILES_FILE_STORE_LOCATION_ID
	attachedToTypeId = process.env.REACT_APP_LEASE_FILES_ATTACHED_TO_TYPE_ID

	constructor(props: any) {
		super(props)
		this.state = {
			selectedLease: null,
			calculations: null,
			calculationsLoading: false,
			showForm: false,
			editing: false,
			submitting: false,
			confirmTerminate: false,
			UnitNumber: "",
			DateReceived: "",
			LeaseLength: "",
			MonthOfFirstPayment: "",
			CapitalizedAmount: "",
			LeaseDepreciationRate: "",
			InterestRate: "",
			MgmtFeePercent: "",
			MaintenanceProgramMonthly: "",
			SalesTaxPerMonth: "",
			SalesTaxOnIncentive: "",
			PeriodZeroLeaseExpense: "",
			PeriodZeroDepreciation: "",
			EndOfLeaseServiceCharge: "",
			EFMNegotiatedRate: "",
			DownPayment: "",
			GainOnPriorVehicle: "",
			leaseRetirementDate: "",
			overrideStep: 0,
			calculatedPayment: null,
			overrideValue: "",
			savedLeaseId: null,
			errorMessage: "",
			leaseTerminationRecordTypeId: null,
			showPreview: false,
			proposedLease: null,
			proposedCalculations: null,
			attachments: false,
			leaseReceipts: [],
			filePath: null,
			fileName: null,
		}
	}

	getStatusColor = (status: string) => {
		if (status === "Active")
			return { backgroundColor: "#d4edda", color: "inherit" }
		if (status === "Terminated")
			return { backgroundColor: "#f8d7da", color: "#721c24" }
		return { backgroundColor: "inherit", color: "inherit" }
	}

	selectLease = (lease: any) => {
		this.setState({
			selectedLease: lease,
			calculations: null,
			calculationsLoading: true,
			leaseReceipts: [],
		})
		axios
			.get(
				`${process.env.REACT_APP_API_URL}/api/fleet/leases/${lease.VehicleLeaseId}/calculations`,
				AxiosConfig.configValue("min"),
			)
			.then((res) => {
				this.setState({ calculations: res.data, calculationsLoading: false })
				this.getLeaseAttachments(lease.VehicleLeaseId)
			})
			.catch((err) => {
				this.setState({ calculationsLoading: false, errorMessage: "Error fetching calculations and attachments" })
				AxiosConfig.handleError(err)
			})
	}

	openAddForm = () => {
		this.setState({ showForm: true, editing: false, errorMessage: "" })
	}

	openEditForm = () => {
		const l = this.state.selectedLease
		this.setState({
			showForm: true,
			editing: true,
			errorMessage: "",
			UnitNumber: l.UnitNumber || "",
			DateReceived: moment(l.DateReceived).format("YYYY-MM-DD"),
			LeaseLength: String(l.LeaseLength),
			MonthOfFirstPayment: moment(l.MonthOfFirstPayment).format("YYYY-MM-DD"),
			CapitalizedAmount: String(l.CapitalizedAmount),
			LeaseDepreciationRate: String(l.LeaseDepreciationRate * 100),
			InterestRate: String(l.InterestRate * 100),
			MgmtFeePercent: String(l.MgmtFeePercent * 100),
			MaintenanceProgramMonthly: String(l.MaintenanceProgramMonthly),
			SalesTaxPerMonth: String(l.SalesTaxPerMonth),
			SalesTaxOnIncentive: String(l.SalesTaxOnIncentive),
			PeriodZeroLeaseExpense: String(l.PeriodZeroLeaseExpense),
			PeriodZeroDepreciation: String(l.PeriodZeroDepreciation),
			EndOfLeaseServiceCharge: String(l.EndOfLeaseServiceCharge),
			EFMNegotiatedRate: String(l.EFMNegotiatedRate),
			DownPayment: String(l.DownPayment),
			GainOnPriorVehicle: String(l.GainOnPriorVehicle),
		})
	}

	closeForm = () => {
		this.setState({ showForm: false })
	}

	handleFieldChange = (field: string, value: string) => {
		this.setState({ [field]: value } as any)
	}

	handlePaste = (field: string) => (e: React.ClipboardEvent) => {
		const pasted = e.clipboardData.getData("text")
		const cleaned = pasted.replace(/[^\d.-]/g, "")
		if (pasted !== cleaned) {
			e.preventDefault()
			this.setState({ [field]: cleaned } as any)
		}
	}

	submitForm = () => {
		this.setState({ submitting: true })
		const body: any = {
			...this.state.selectedLease,
			UnitNumber: this.state.UnitNumber,
			DateReceived: this.state.DateReceived,
			LeaseLength: parseInt(this.state.LeaseLength, 10),
			MonthOfFirstPayment: this.state.MonthOfFirstPayment,
			CapitalizedAmount: parseFloat(this.state.CapitalizedAmount),
			LeaseDepreciationRate: parseFloat(this.state.LeaseDepreciationRate) / 100,
			InterestRate: parseFloat(this.state.InterestRate) / 100,
			MgmtFeePercent: parseFloat(this.state.MgmtFeePercent) / 100,
			MaintenanceProgramMonthly: parseFloat(
				this.state.MaintenanceProgramMonthly,
			),
			SalesTaxPerMonth: parseFloat(this.state.SalesTaxPerMonth),
			SalesTaxOnIncentive: parseFloat(this.state.SalesTaxOnIncentive),
			PeriodZeroLeaseExpense: parseFloat(this.state.PeriodZeroLeaseExpense),
			PeriodZeroDepreciation: parseFloat(this.state.PeriodZeroDepreciation),
			EndOfLeaseServiceCharge: parseFloat(this.state.EndOfLeaseServiceCharge),
			EFMNegotiatedRate: parseFloat(this.state.EFMNegotiatedRate),
			DownPayment: parseFloat(this.state.DownPayment),
			GainOnPriorVehicle: parseFloat(this.state.GainOnPriorVehicle),
		}

		axios
			.post(
				`${process.env.REACT_APP_API_URL}/api/fleet/leases/calculations/preview`,
				body,
				AxiosConfig.configValue("min"),
			)
			.then((res) => {
				this.setState({
					showForm: false,
					submitting: false,
					overrideStep: 1,
					calculatedPayment: res.data.TotalMonthlyLeasePayment,
					proposedLease: body,
					proposedCalculations: res.data,
				})
			})
			.catch((err) => {
				this.setState({
					submitting: false,
					showForm: true,
					errorMessage: err.response?.data?.Message || "An error occurred.",
				})
			})
	}

	postLease = () => {
		if (!this.state.proposedLease) return
		const body = {
			...this.state.proposedLease,
			VehicleInformationId: this.props.info.VehicleInformationId,
		}
		axios
			.post(
				`${process.env.REACT_APP_API_URL}/api/fleet/leases`,
				body,
				AxiosConfig.configValue("min"),
			)
			.then(() => {
				this.setState({
					showPreview: false,
					proposedLease: null,
					proposedCalculations: null,
					overrideStep: 0,
					overrideValue: "",
					calculatedPayment: null,
					errorMessage: "",
				})
				this.props.refreshAll("leases")
				this.props.refreshAll("details")
			})
			.catch((err) => {
				this.setState({
					errorMessage: err.response?.data?.Message || "An error occurred.",
				})
			})
	}

	editLease = () => {
		if (!this.state.proposedLease || !this.state.selectedLease) return
		axios
			.post(
				`${process.env.REACT_APP_API_URL}/api/fleet/leases/${this.state.selectedLease.VehicleLeaseId}/request-edit`,
				this.state.proposedLease,
				AxiosConfig.configValue("min"),
			)
			.then(() => {
				this.setState({
					showPreview: false,
					proposedLease: null,
					proposedCalculations: null,
					selectedLease: null,
					calculations: null,
					overrideStep: 0,
					overrideValue: "",
					calculatedPayment: null,
					editing: false,
					errorMessage: "",
				})
				this.props.refreshAll("leases")
			})
			.catch((err) => {
				this.setState({
					errorMessage: err.response?.data?.Message || "An error occurred.",
				})
			})
	}

	applyOverrideAndPreview = () => {
		if (!this.state.proposedLease) return
		const updatedLease = {
			...this.state.proposedLease,
			MonthlyExpenseOverride: parseFloat(this.state.overrideValue),
		}
		this.setState({
			overrideStep: 0,
			proposedLease: updatedLease,
			proposedCalculations: null,
			showPreview: true,
		})
		axios
			.post(
				`${process.env.REACT_APP_API_URL}/api/fleet/leases/calculations/preview`,
				updatedLease,
				AxiosConfig.configValue("min"),
			)
			.then((res) => {
				this.setState({ proposedCalculations: res.data })
			})
			.catch((err) => {
				this.setState({
					errorMessage: err.response?.data?.Message || "An error occurred.",
				})
			})
	}

	terminateLease = () => {
		if (!this.state.selectedLease) return
		if (this.state.submitting) return
		this.setState({ submitting: true, errorMessage: "" })
		axios
			.post(
				`${process.env.REACT_APP_API_URL}/api/fleet/leases/${this.state.selectedLease.VehicleLeaseId}/request-terminate`,
				JSON.stringify(this.state.leaseRetirementDate),
				{
					headers: {
						...AxiosConfig.configValue("min").headers,
						"Content-Type": "application/json",
					},
				},
			)
			.then(() => {
				this.setState({
					selectedLease: null,
					calculations: null,
					confirmTerminate: false,
					leaseRetirementDate: "",
					submitting: false,
				})
				this.props.refreshAll("leases")
			})
			.catch((err) => {
				this.setState({
					submitting: false,
					errorMessage: err.response?.data?.Message || "An error occurred.",
				})
			})
	}

	closePreview = () => {
		this.setState({
			showPreview: false,
			proposedLease: null,
			proposedCalculations: null,
		})
	}

	getLeaseAttachments = (vehicleLeaseId: number) => {
		axios
			.get(
				`${process.env.REACT_APP_API_URL}/api/fleet/leases/${vehicleLeaseId}/attachments`,
				AxiosConfig.configValue("min"),
			)
			.then((res) => {
				if (this.state.selectedLease?.VehicleLeaseId !== vehicleLeaseId) return;
				this.setState({ leaseReceipts: res.data, errorMessage: "" })
			})
			.catch((err) => {
				this.setState({ errorMessage: "Error fetching attachments" })
				AxiosConfig.handleError(err);
			})
	}

	deleteLeaseAttachments = (attachmentId: number) => {
		if (!this.state.selectedLease) return;
		axios
			.post(
				`${process.env.REACT_APP_API_URL}/api/fleet/leases/${this.state.selectedLease.VehicleLeaseId}/attachments/${attachmentId}/delete`,
				null,
				AxiosConfig.configValue("min"),
			)
			.then(() => {
				this.setState({ leaseReceipts: this.state.leaseReceipts.filter(x => x.AttachmentID !== attachmentId), errorMessage: "" })
			})
			.catch((err) => {
				this.setState({ errorMessage: "Error deleting attachment" })
				AxiosConfig.handleError(err);
			})
	}

	toggleDocUploader = () => {
		if (this.state.attachments) {
			this.setState({ attachments: false })
			if (this.state.selectedLease) {
				this.getLeaseAttachments(this.state.selectedLease.VehicleLeaseId)
			}
		} else {
			this.setState({ attachments: true })
		}
	}

	imageExtensions = ["jpg", "jpeg", "png", "svg", "ico", "gif", "apng"]

	isImage = (extension: string | null) => {
		if (extension && this.imageExtensions.includes(extension)) return true
		return false
	}

	renderPreviewModal = () => {
		if (!this.state.showPreview) return null
		const { proposedLease, selectedLease, calculations, proposedCalculations } =
			this.state
		if (!proposedLease) return null

		return (
			<Modal
				show
				onHide={this.closePreview}
				dialogClassName="full-screen-modal lease-preview-modal"
			>
				<Modal.Header closeButton>
					<Modal.Title>Lease Preview</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<LeaseInformationView
						lease={proposedLease}
						calculations={proposedCalculations}
						loading={!proposedCalculations}
						compareTo={
							selectedLease
								? {
										lease: {
											...selectedLease,
											DateReceived: moment(selectedLease.DateReceived).format(
												"YYYY-MM-DD",
											),
											MonthOfFirstPayment: moment(
												selectedLease.MonthOfFirstPayment,
											).format("YYYY-MM-DD"),
										},
										calculations,
									}
								: undefined
						}
					/>
				</Modal.Body>
				<Modal.Footer>
					{this.state.errorMessage && (
						<span className="terminate-error">{this.state.errorMessage}</span>
					)}
					{!this.state.editing && (
						<Button variant="primary" onClick={this.postLease}>
							Save
						</Button>
					)}
					{this.state.editing && (
						<Button variant="primary" onClick={this.editLease}>
							Send Edit Request
						</Button>
					)}
					<Button variant="secondary" onClick={this.closePreview}>
						Cancel
					</Button>
				</Modal.Footer>
			</Modal>
		)
	}

	render() {
		return (
			<div className="VehicleLeaseInformation">
				{this.state.attachments && this.state.selectedLease && (
					<DocUploader
						toggleDocUploader={this.toggleDocUploader}
						maintenanceID={this.state.selectedLease.VehicleLeaseId}
						contextUserId={this.props.userId}
						refreshTimeline={() => null}
						storageLocation={this.storageLocation || ""}
						containerName={this.containerName || ""}
						fileStoreLocationId={this.fileStoreLocationId || ""}
						attachedToTypeId={this.attachedToTypeId}
					/>
				)}

				{!this.state.selectedLease && (
					<div>
						{this.props.leasesLoading && (
							<Loader type="infinite" title="Loading lease information..." />
						)}
						{!this.props.leasesLoading && this.props.leases.length === 0 && (
							<div className="NoVehicleLeaseInformationWrapper">
								<p>No lease records found for this vehicle.</p>
								<Button variant="primary" onClick={this.openAddForm}>
									Add Lease
								</Button>
							</div>
						)}
						{/* ========== Lease List — clickable rows of all leases for this vehicle ========== */}
						{!this.props.leasesLoading && this.props.leases.length > 0 && (
							<div>
								<h4 className="unit-number-heading">
									Unit Number: {this.props.info.UnitNumber}
									{shouldAllowAccess("FLEET:LEASEADMIN") && (
										<RenameUnitNumberButton
											vehicleInformationId={this.props.info.VehicleInformationId}
											currentUnitNumber={this.props.info.UnitNumber}
											onSuccess={() => {
												this.props.refreshAll("leases")
												this.props.refreshAll("details")
											}}
										/>
									)}
								</h4>
								<Table striped bordered hover>
									<thead>
										<tr>
											<th>Status</th>
											<th>Date Received</th>
											<th>Month of First Payment</th>
											<th>Lease Length</th>
										</tr>
									</thead>
									<tbody>
										{this.props.leases.map((lease: any) => (
											<tr
												key={lease.VehicleLeaseId}
												onClick={() => this.selectLease(lease)}
											>
												<td
													style={{
														...this.getStatusColor(lease.Status),
														display: "flex",
														justifyContent: "space-between",
														alignItems: "center",
													}}
												>
													<span>{lease.Status}</span>
													{lease.HasPendingRequest && (
														<Badge variant="warning">Pending Request</Badge>
													)}
												</td>
												<td>
													{moment(lease.DateReceived).format("MM/DD/YYYY")}
												</td>
												<td>
													{moment(lease.MonthOfFirstPayment).format(
														"MM/DD/YYYY",
													)}
												</td>
												<td>{lease.LeaseLength}</td>
											</tr>
										))}
									</tbody>
								</Table>
								{!this.props.leases.some((l) => l.Status === "Active") && (
									<Button variant="primary" onClick={this.openAddForm}>
										Add Lease
									</Button>
								)}
							</div>
						)}
					</div>
				)}

				{this.state.selectedLease && (
					<Modal
						show={true}
						onHide={() =>
							this.setState({ selectedLease: null, calculations: null, attachments: false })
						}
						dialogClassName="full-screen-modal"
					>
						<Modal.Body>
							<div>
								<div className="lease-details-header">
									<Button
										variant="secondary"
										onClick={() =>
											this.setState({ selectedLease: null, calculations: null, attachments: false })
										}
									>
										Back
									</Button>
									<Button variant="link" onClick={this.toggleDocUploader}>
										<span className="material-icons">attach_file</span>
									</Button>
									{this.state.selectedLease.HasPendingRequest ? (
										<OverlayTrigger
											placement="bottom"
											overlay={
												<Tooltip id="edit-disabled-tooltip">
													This lease has a pending request. Further changes are
													locked until it's resolved.
												</Tooltip>
											}
										>
											<span className="d-inline-block">
												<Button
													variant="primary"
													disabled
													style={{ pointerEvents: "none" }}
												>
													Request Edit
												</Button>
											</span>
										</OverlayTrigger>
									) : (
										<Button variant="primary" onClick={this.openEditForm}>
											Request Edit
										</Button>
									)}
									{this.state.selectedLease.Status !== "Terminated" &&
										(this.state.selectedLease.HasPendingRequest ? (
											<OverlayTrigger
												placement="bottom"
												overlay={
													<Tooltip id="terminate-disabled-tooltip">
														This lease has a pending request. Further changes
														are locked until it's resolved.
													</Tooltip>
												}
											>
												<span className="d-inline-block">
													<Button
														variant="danger"
														disabled
														style={{ pointerEvents: "none" }}
													>
														Request Terminate
													</Button>
												</span>
											</OverlayTrigger>
										) : (
											<Button
												variant="danger"
												onClick={() =>
													this.setState({
														confirmTerminate: true,
														errorMessage: "",
													})
												}
											>
												Request Terminate
											</Button>
										))}
								</div>
								{this.state.errorMessage && (
									<div className="terminate-error">{this.state.errorMessage}</div>
								)}
								{this.state.confirmTerminate && (
									<Modal
										show={true}
										backdrop="static"
										dialogClassName="terminate-modal"
									>
										<Modal.Header>
											<Modal.Title>Confirm Termination Request</Modal.Title>
										</Modal.Header>
										<Modal.Body>
											<p>Submit request to terminate lease?</p>
											<label htmlFor="leaseRetirementDate">
												Termination date
											</label>
											<FormControl
												id="leaseRetirementDate"
												type="date"
												value={this.state.leaseRetirementDate}
												onChange={(e) =>
													this.setState({ leaseRetirementDate: e.target.value })
												}
											/>
											{this.state.errorMessage && (
												<div className="terminate-error">
													{this.state.errorMessage}
												</div>
											)}
										</Modal.Body>
										<Modal.Footer>
											<Button
												variant="danger"
												onClick={this.terminateLease}
												disabled={
													!this.state.leaseRetirementDate ||
													this.state.submitting
												}
											>
												{this.state.submitting ? "Submitting..." : "Terminate"}
											</Button>
											<Button
												variant="secondary"
												onClick={() =>
													this.setState({
														confirmTerminate: false,
														leaseRetirementDate: "",
													})
												}
											>
												Cancel
											</Button>
										</Modal.Footer>
									</Modal>
								)}
								<LeaseInformationView
									lease={this.state.selectedLease}
									calculations={this.state.calculations}
									loading={this.state.calculationsLoading}
								/>
								{this.state.leaseReceipts.length > 0 && (
									<div className="lease-attachments-section">
										<h5 className="lease-attachments-header">Attachments</h5>
										<div className="VM-Card-Receipt-Pics">
											{this.state.leaseReceipts.map((receipt, i) => (
												<div key={`lease-receipt-${receipt.AttachmentID}`} className="lease-receipt-pic">
													<span
														className="lease-receipt-delete material-icons"
														onClick={async () => {
															const confirmed = await new Dialog({ message: "Are you sure you want to delete this attachment?", trueText: "Delete", falseText: "Cancel" }).show()
															if (confirmed) this.deleteLeaseAttachments(receipt.AttachmentID)
														}}
													>
														close
													</span>
													{this.isImage(reggie.getFileExtensionFromString(receipt.RelativePath)) ? (
														<img
															src={receipt.ImageFullUrl ?? ""}
															alt={receipt.Name}
															onClick={() => openLink(receipt.ImageFullUrl || "")}
															style={{ cursor: "pointer" }}
														/>
													) : (
														<OverlayTrigger
															trigger={["hover", "focus"]}
															placement="auto-end"
															overlay={<Tooltip id={`receipt-tooltip-${i}`}>{receipt.Name}</Tooltip>}
														>
															<Button
																variant="link"
																onClick={() => openLink(receipt.ImageFullUrl || "")}
															>
																<IconBuilder
																	extension={reggie.getFileExtensionFromString(receipt.RelativePath) ?? "file"}
																	size={64}
																/>
															</Button>
														</OverlayTrigger>
													)}
												</div>
											))}
										</div>
									</div>
								)}
							</div>
						</Modal.Body>
					</Modal>
				)}

				{/* ========== Add/Edit Modal — form for creating or updating a lease record ========== */}
				{this.state.showForm && (
					<Modal
						show={true}
						backdrop="static"
						size="lg"
						dialogClassName="edit-lease-modal"
					>
						<Modal.Header>
							<Modal.Title>
								{this.state.editing ? "Edit Lease" : "New Lease"}
							</Modal.Title>
						</Modal.Header>
						<Modal.Body>
							<label htmlFor="unitNumber">Unit Number</label>
							<FormControl
								id="unitNumber"
								value={this.state.UnitNumber}
								disabled={this.state.editing}
								onChange={(e) =>
									this.handleFieldChange("UnitNumber", e.target.value)
								}
							/>
							{this.state.editing && (
								<small className="text-muted">
									Unit number can only be changed using the Rename Unit Number
									button on the lease information page.
								</small>
							)}
							<label htmlFor="dateReceived">Date Received</label>
							<FormControl
								id="dateReceived"
								type="date"
								value={this.state.DateReceived}
								onChange={(e) =>
									this.handleFieldChange("DateReceived", e.target.value)
								}
							/>
							<label htmlFor="leaseLength">Lease Length (months)</label>
							<FormControl
								id="leaseLength"
								type="number"
								value={this.state.LeaseLength}
								onPaste={this.handlePaste("LeaseLength")}
								onChange={(e) =>
									this.handleFieldChange("LeaseLength", e.target.value)
								}
							/>
							<label htmlFor="monthOfFirstPayment">
								Month of First Payment
							</label>
							<FormControl
								id="monthOfFirstPayment"
								type="date"
								value={this.state.MonthOfFirstPayment}
								onChange={(e) =>
									this.handleFieldChange("MonthOfFirstPayment", e.target.value)
								}
							/>
							<label htmlFor="capitalizedAmount">Capitalized Amount</label>
							<FormControl
								id="capitalizedAmount"
								type="number"
								step="any"
								value={this.state.CapitalizedAmount}
								onPaste={this.handlePaste("CapitalizedAmount")}
								onChange={(e) =>
									this.handleFieldChange("CapitalizedAmount", e.target.value)
								}
							/>
							<label htmlFor="leaseDepreciationRate">
								Depreciation Rate (%)
							</label>
							<FormControl
								id="leaseDepreciationRate"
								type="number"
								step="any"
								value={this.state.LeaseDepreciationRate}
								onPaste={this.handlePaste("LeaseDepreciationRate")}
								onChange={(e) =>
									this.handleFieldChange(
										"LeaseDepreciationRate",
										e.target.value,
									)
								}
							/>
							<label htmlFor="interestRate">Interest Rate (%)</label>
							<FormControl
								id="interestRate"
								type="number"
								step="any"
								value={this.state.InterestRate}
								onPaste={this.handlePaste("InterestRate")}
								onChange={(e) =>
									this.handleFieldChange("InterestRate", e.target.value)
								}
							/>
							<label htmlFor="mgmtFeePercent">Management Fee %</label>
							<FormControl
								id="mgmtFeePercent"
								type="number"
								step="any"
								value={this.state.MgmtFeePercent}
								onPaste={this.handlePaste("MgmtFeePercent")}
								onChange={(e) =>
									this.handleFieldChange("MgmtFeePercent", e.target.value)
								}
							/>
							<label htmlFor="maintenanceProgramMonthly">
								Maintenance Program Monthly
							</label>
							<FormControl
								id="maintenanceProgramMonthly"
								type="number"
								step="any"
								value={this.state.MaintenanceProgramMonthly}
								onPaste={this.handlePaste("MaintenanceProgramMonthly")}
								onChange={(e) =>
									this.handleFieldChange(
										"MaintenanceProgramMonthly",
										e.target.value,
									)
								}
							/>
							<label htmlFor="salesTaxPerMonth">Sales Tax Per Month</label>
							<FormControl
								id="salesTaxPerMonth"
								type="number"
								step="any"
								value={this.state.SalesTaxPerMonth}
								onPaste={this.handlePaste("SalesTaxPerMonth")}
								onChange={(e) =>
									this.handleFieldChange("SalesTaxPerMonth", e.target.value)
								}
							/>
							<label htmlFor="salesTaxOnIncentive">
								Sales Tax On Incentive
							</label>
							<FormControl
								id="salesTaxOnIncentive"
								type="number"
								step="any"
								value={this.state.SalesTaxOnIncentive}
								onPaste={this.handlePaste("SalesTaxOnIncentive")}
								onChange={(e) =>
									this.handleFieldChange("SalesTaxOnIncentive", e.target.value)
								}
							/>
							<label htmlFor="periodZeroLeaseExpense">
								Period Zero Lease Expense
							</label>
							<FormControl
								id="periodZeroLeaseExpense"
								type="number"
								step="any"
								value={this.state.PeriodZeroLeaseExpense}
								onPaste={this.handlePaste("PeriodZeroLeaseExpense")}
								onChange={(e) =>
									this.handleFieldChange(
										"PeriodZeroLeaseExpense",
										e.target.value,
									)
								}
							/>
							<label htmlFor="periodZeroDepreciation">
								Period Zero Depreciation
							</label>
							<FormControl
								id="periodZeroDepreciation"
								type="number"
								step="any"
								value={this.state.PeriodZeroDepreciation}
								onPaste={this.handlePaste("PeriodZeroDepreciation")}
								onChange={(e) =>
									this.handleFieldChange(
										"PeriodZeroDepreciation",
										e.target.value,
									)
								}
							/>
							<label htmlFor="endOfLeaseServiceCharge">
								End of Lease Service Charge
							</label>
							<FormControl
								id="endOfLeaseServiceCharge"
								type="number"
								step="any"
								value={this.state.EndOfLeaseServiceCharge}
								onPaste={this.handlePaste("EndOfLeaseServiceCharge")}
								onChange={(e) =>
									this.handleFieldChange(
										"EndOfLeaseServiceCharge",
										e.target.value,
									)
								}
							/>
							<label htmlFor="efmNegotiatedRate">EFM Negotiated Rate</label>
							<FormControl
								id="efmNegotiatedRate"
								type="number"
								step="any"
								value={this.state.EFMNegotiatedRate}
								onPaste={this.handlePaste("EFMNegotiatedRate")}
								onChange={(e) =>
									this.handleFieldChange("EFMNegotiatedRate", e.target.value)
								}
							/>
							<label htmlFor="downPayment">Down Payment</label>
							<FormControl
								id="downPayment"
								type="number"
								step="any"
								value={this.state.DownPayment}
								onPaste={this.handlePaste("DownPayment")}
								onChange={(e) =>
									this.handleFieldChange("DownPayment", e.target.value)
								}
							/>
							<label htmlFor="gainOnPriorVehicle">Gain On Prior Vehicle</label>
							<FormControl
								id="gainOnPriorVehicle"
								type="number"
								step="any"
								value={this.state.GainOnPriorVehicle}
								onPaste={this.handlePaste("GainOnPriorVehicle")}
								onChange={(e) =>
									this.handleFieldChange("GainOnPriorVehicle", e.target.value)
								}
							/>
						</Modal.Body>
						<Modal.Footer>
							{this.state.errorMessage && (
								<span className="terminate-error">
									{this.state.errorMessage}
								</span>
							)}
							<Button
								variant="primary"
								onClick={this.submitForm}
								disabled={
									this.state.submitting ||
									!this.state.UnitNumber ||
									!this.state.DateReceived ||
									!this.state.LeaseLength ||
									!this.state.MonthOfFirstPayment ||
									!this.state.CapitalizedAmount ||
									!this.state.LeaseDepreciationRate ||
									!this.state.InterestRate ||
									!this.state.MgmtFeePercent ||
									!this.state.MaintenanceProgramMonthly ||
									!this.state.SalesTaxPerMonth ||
									!this.state.SalesTaxOnIncentive ||
									!this.state.PeriodZeroLeaseExpense ||
									!this.state.PeriodZeroDepreciation ||
									!this.state.EndOfLeaseServiceCharge ||
									!this.state.EFMNegotiatedRate ||
									!this.state.DownPayment ||
									!this.state.GainOnPriorVehicle
								}
							>
								Continue
							</Button>
							<Button variant="secondary" onClick={this.closeForm}>
								Cancel
							</Button>
						</Modal.Footer>
					</Modal>
				)}
				{/* ========== Monthly Expense Override confirmation after creating or updating a lease record ========== */}
				{this.state.overrideStep > 0 && (
					<Modal show={true} backdrop="static">
						<Modal.Header>
							<Modal.Title>Confirm Monthly Expense</Modal.Title>
						</Modal.Header>
						<Modal.Body>
							{this.state.overrideStep === 1 && (
								<div style={{ textAlign: "center" }}>
									<p>
										Based on your entered input, the calculated value for this
										lease's monthly expense is:
									</p>
									<h3>
										$
										{this.state.calculatedPayment?.toLocaleString(undefined, {
											minimumFractionDigits: 2,
											maximumFractionDigits: 2,
										})}
									</h3>
									<p>
										Is the above value equal to the{" "}
										<strong>
											Total Monthly Rental Including Additional Services
										</strong>{" "}
										value under section 4 of the{" "}
										<strong>Vehicle Lease Contract</strong> for this vehicle?
									</p>
								</div>
							)}
							{this.state.overrideStep === 2 && (
								<div>
									<p>
										In the Vehicle Lease Contract, enter the value seen for
										<strong>
											Total Monthly Rental Including Additional Services
										</strong>
										:
									</p>
									<div
										style={{
											display: "flex",
											alignItems: "center",
											gap: "5px",
										}}
									>
										<span>$</span>
										<FormControl
											type="number"
											step="any"
											value={this.state.overrideValue}
											onChange={(e) =>
												this.setState({ overrideValue: e.target.value })
											}
										/>
									</div>
								</div>
							)}
							{this.state.overrideStep === 3 && (
								<div>
									<p>
										There is a{" "}
										<span style={{ color: "#d82c26" }}>
											$
											{Math.abs(
												(this.state.calculatedPayment || 0) -
													parseFloat(this.state.overrideValue || "0"),
											).toFixed(2)}{" "}
										</span>
										difference between the calculated value of Total Monthly
										Rental Including Additional Services versus the actual value
										seen on the Vehicle Lease Contract.
									</p>
									<p>
										Calculated: $
										{this.state.calculatedPayment?.toLocaleString(undefined, {
											minimumFractionDigits: 2,
											maximumFractionDigits: 2,
										})}
									</p>
									<p>
										Contract: $
										{parseFloat(this.state.overrideValue).toLocaleString(
											undefined,
											{
												minimumFractionDigits: 2,
												maximumFractionDigits: 2,
											},
										)}
									</p>
									<p>
										Would you like to override the calculated value to your
										entered actual value as seen below?
									</p>
									<h3 style={{ textAlign: "center" }}>
										$
										{parseFloat(this.state.overrideValue).toLocaleString(
											undefined,
											{ minimumFractionDigits: 2, maximumFractionDigits: 2 },
										)}
									</h3>
								</div>
							)}
						</Modal.Body>
						<Modal.Footer>
							{this.state.overrideStep === 1 && (
								<>
									<Button
										variant="primary"
										onClick={() => {
											this.setState({
												overrideStep: 0,
												showPreview: true,
											})
										}}
									>
										Yes
									</Button>
									<Button
										variant="secondary"
										onClick={() => this.setState({ overrideStep: 2 })}
									>
										No
									</Button>
								</>
							)}
							{this.state.overrideStep === 2 && (
								<Button
									variant="primary"
									disabled={!this.state.overrideValue}
									onClick={() =>
										this.setState({
											overrideStep: 3,
										})
									}
								>
									Confirm
								</Button>
							)}
							{this.state.overrideStep === 3 && (
								<>
									<Button
										variant="primary"
										onClick={this.applyOverrideAndPreview}
									>
										Override
									</Button>
									<Button
										variant="danger"
										onClick={() =>
											this.setState({ overrideStep: 1, overrideValue: "" })
										}
									>
										Back
									</Button>
								</>
							)}
						</Modal.Footer>
					</Modal>
				)}
				{/* ========== Preview modal ========== */}
				{this.renderPreviewModal()}
			</div>
		)
	}
}

export default VehicleLeaseInformation
